import { Activity } from 'lucide-react';
import React, { useMemo, useRef } from 'react';
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import type { TooltipProps } from 'recharts';

import type { TimeTravelPoint } from '../types';
import { formatYield } from '../utils/formatters';
import { ChartDownloadMenu } from '../../../../shared/ui';
import { useChartExport } from '../../../../shared/hooks/useChartExport';
import { buildChartFilename } from '../../../../shared/utils/chartFilename';

interface TimeTravelChartProps {
    data: TimeTravelPoint[] | undefined;
}

// --- 1. Institutional Custom Tooltip ---
const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border border-slate-200 dark:border-slate-700 p-3 rounded-xl shadow-xl text-sm min-w-[200px] z-50">
                <div className="font-bold border-b border-slate-200 dark:border-slate-700 pb-2 mb-2 text-slate-800 dark:text-slate-200 uppercase tracking-wider text-xs">
                    Maturity Tenor:{' '}
                    <span className="text-indigo-600 dark:text-indigo-400">{label}</span>
                </div>
                <div className="space-y-1.5 flex flex-col">
                    {payload.map((entry, index) => {
                        if (entry.value == null) return null;
                        return (
                            <div
                                key={index}
                                className="flex justify-between items-center gap-4 text-xs"
                            >
                                <span className="flex items-center gap-1.5 font-medium text-slate-600 dark:text-slate-300">
                                    <span
                                        className="w-2 h-2 rounded-full shadow-sm inline-block"
                                        style={{ backgroundColor: entry.color }}
                                    ></span>
                                    {entry.name}
                                </span>
                                <span className="font-mono font-bold text-slate-900 dark:text-white tracking-tight">
                                    {formatYield(entry.value)}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }
    return null;
};

// --- 2. Main Chart Component ---
export const TimeTravelChart: React.FC<TimeTravelChartProps> = ({ data }) => {
    const chartData = useMemo(() => data || [], [data]);

    const containerRef = useRef<HTMLDivElement>(null);
    const { downloadPng, downloadJpeg } = useChartExport(containerRef);

    // Dynamically calculate the Y-axis domain to prevent charts from flattening out.
    const yDomain = useMemo(() => {
        if (!chartData.length) return [0, 5];

        let min = Infinity;
        let max = -Infinity;

        chartData.forEach((point) => {
            const vals = [
                point.current,
                point.week_1_ago,
                point.month_1_ago,
                point.month_6_ago,
                point.year_1_ago,
            ].filter((v): v is number => v !== null && v !== undefined);

            if (vals.length > 0) {
                min = Math.min(min, ...vals);
                max = Math.max(max, ...vals);
            }
        });

        if (min === Infinity) return [0, 5];

        // Add a 0.25% buffer top and bottom for visual breathing room
        return [
            Math.max(0, Math.floor((min - 0.25) * 10) / 10),
            Math.ceil((max + 0.25) * 10) / 10,
        ];
    }, [chartData]);

    if (!chartData.length) {
        return (
            <div className="flex h-full min-h-[450px] items-center justify-center text-slate-400 text-sm font-medium border border-dashed border-slate-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900">
                No curve data available.
            </div>
        );
    }

    // Institutional Color Palette for dark/light mode contrast
    const colors = {
        current: '#4f46e5', // Indigo-600
        week_1: '#0ea5e9', // Sky-500
        month_1: '#10b981', // Emerald-500
        month_6: '#f59e0b', // Amber-500
        year_1: '#64748b', // Slate-500
    };

    return (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm p-5 md:p-6 flex flex-col h-full min-h-[450px]">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6 border-b border-slate-100 dark:border-slate-800 pb-4">
                <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-lg">
                    <Activity className="w-5 h-5" />
                </div>
                <div>
                    <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 tracking-tight">
                        Yield Curve Evolution
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Compare today's term structure against historical periods.
                    </p>
                </div>
                <div className="ml-auto">
                    <ChartDownloadMenu
                        label="Yield Curve Evolution chart"
                        onPng={() => downloadPng({ filename: buildChartFilename('yield-curve-evolution') })}
                        onJpeg={() => downloadJpeg({ filename: buildChartFilename('yield-curve-evolution') })}
                    />
                </div>
            </div>

            {/* Chart Engine */}
            <div ref={containerRef} className="flex-1 w-full relative min-h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={chartData}
                        margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                    >
                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="currentColor"
                            className="text-slate-200 dark:text-slate-700/50"
                        />

                        <XAxis
                            dataKey="tenor"
                            tick={{ fontSize: 12, fontWeight: 500 }}
                            tickMargin={12}
                            axisLine={false}
                            tickLine={false}
                            stroke="currentColor"
                            className="text-slate-400 dark:text-slate-500"
                        />

                        <YAxis
                            domain={yDomain}
                            tickFormatter={(value) => `${value.toFixed(1)}%`}
                            tick={{ fontSize: 12, fontWeight: 500 }}
                            axisLine={false}
                            tickLine={false}
                            stroke="currentColor"
                            className="text-slate-400 dark:text-slate-500"
                        />

                        <Tooltip
                            content={<CustomTooltip />}
                            cursor={{
                                stroke: '#64748b',
                                strokeWidth: 1,
                                strokeDasharray: '4 4',
                            }}
                        />

                        <Legend
                            verticalAlign="bottom"
                            height={36}
                            iconType="circle"
                            wrapperStyle={{
                                fontSize: '13px',
                                fontWeight: 500,
                                paddingTop: '20px',
                            }}
                        />

                        {/* Oldest lines rendered first so they sit in the background visually */}
                        <Line
                            type="monotone"
                            dataKey="year_1_ago"
                            name="1 Year Ago"
                            stroke={colors.year_1}
                            strokeWidth={1.5}
                            strokeDasharray="4 4"
                            dot={false}
                            activeDot={{ r: 4 }}
                            isAnimationActive={false}
                            connectNulls
                        />
                        <Line
                            type="monotone"
                            dataKey="month_6_ago"
                            name="6 Months Ago"
                            stroke={colors.month_6}
                            strokeWidth={1.5}
                            strokeDasharray="2 2"
                            dot={false}
                            activeDot={{ r: 4 }}
                            isAnimationActive={false}
                            connectNulls
                        />
                        <Line
                            type="monotone"
                            dataKey="month_1_ago"
                            name="1 Month Ago"
                            stroke={colors.month_1}
                            strokeWidth={2}
                            dot={{ r: 2, fill: colors.month_1, strokeWidth: 0 }}
                            activeDot={{ r: 5 }}
                            isAnimationActive={false}
                            connectNulls
                        />
                        <Line
                            type="monotone"
                            dataKey="week_1_ago"
                            name="1 Week Ago"
                            stroke={colors.week_1}
                            strokeWidth={2}
                            dot={{ r: 2, fill: colors.week_1, strokeWidth: 0 }}
                            activeDot={{ r: 5 }}
                            isAnimationActive={false}
                            connectNulls
                        />
                        {/* The Current line is bolded to draw the eye immediately */}
                        <Line
                            type="monotone"
                            dataKey="current"
                            name="Current Curve"
                            stroke={colors.current}
                            strokeWidth={3}
                            dot={{ r: 4, strokeWidth: 2, fill: '#fff', stroke: colors.current }}
                            activeDot={{
                                r: 7,
                                stroke: '#fff',
                                strokeWidth: 2,
                                fill: colors.current,
                            }}
                            isAnimationActive={false}
                            connectNulls
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default TimeTravelChart;
